import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { ArrowRight, Inbox } from 'lucide-react';

export interface CatalogStat {
  label: string;
  value: string | number;
  highlight?: boolean;
}

interface CatalogCardProps {
  title: string;
  subtitle: string;
  href: string;
  icon: React.ElementType;
  stats: CatalogStat[];
  pendingCount?: number;
  badgeLabel?: string;
}

export function CatalogCard({
  title,
  subtitle,
  href,
  icon: Icon,
  stats,
  pendingCount,
  badgeLabel,
}: CatalogCardProps) {
  return (
    <Link
      to={href}
      className="group relative flex flex-col bg-white rounded-xl border border-slate-200 shadow-sm hover:border-brand-teal/50 hover:shadow-md transition-all overflow-hidden"
    >
      <div className="absolute top-3 right-3 flex items-center gap-2">
        {pendingCount != null && pendingCount > 0 && (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
            <Inbox size={10} />
            {pendingCount} pending
          </span>
        )}
        {badgeLabel && (
          <span className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded bg-slate-100 text-slate-500">
            {badgeLabel}
          </span>
        )}
      </div>

      <div className="p-5 flex flex-col h-full">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-brand-teal/10 flex items-center justify-center text-brand-teal flex-shrink-0">
            <Icon size={18} />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-navy-900">{title}</h3>
            <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 mt-auto pt-3 border-t border-slate-100">
          {stats.map((s) => (
            <div key={s.label} className="min-w-0">
              <div className="text-[10px] text-slate-400 uppercase font-bold tracking-wider truncate">
                {s.label}
              </div>
              <div
                className={clsx(
                  'text-lg font-bold truncate',
                  s.highlight ? 'text-amber-600' : 'text-navy-900',
                )}
              >
                {s.value}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-3 flex items-center justify-end text-xs text-brand-teal group-hover:text-brand-teal-dark transition-colors">
          Open
          <ArrowRight size={12} className="ml-1 group-hover:translate-x-0.5 transition-transform" />
        </div>
      </div>
    </Link>
  );
}
